import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, MonitorPlay, Power } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AUTO_BG_MIN_VERSION, AUTO_QUIET_MIN_VERSION, GAP_OPTIONS, getAutopilot, helperVersion, setAutopilot, versionAtLeast } from "@/lib/helper";
import QuietHoursControl from "@/components/QuietHoursControl";

export default function BackgroundModeCard() {
  const version = helperVersion();
  const supported = versionAtLeast(version, AUTO_BG_MIN_VERSION);
  const [auto, setAuto] = useState(null);
  const [gap, setGap] = useState("rand");
  const [limit, setLimit] = useState(30);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    if (!supported) return;
    getAutopilot().then((a) => {
      if (!a) return;
      setAuto(a);
      if (a.gap) setGap(a.gap);
      if (typeof a.dailyLimit === "number") setLimit(a.dailyLimit);
    });
    const t = setInterval(() => getAutopilot().then((a) => a && setAuto(a)), 5000);
    return () => clearInterval(t);
  }, [supported]);

  if (!version) return null;

  const enabled = !!auto?.enabled;

  const changeQuiet = async (quiet) => {
    setAuto((a) => ({ ...a, quiet }));
    const res = await setAutopilot({ quiet }).catch(() => null);
    if (res) setAuto(res);
  };

  const apply = async (on) => {
    setBusy(true);
    try {
      const res = await setAutopilot({ enabled: on, gap, dailyLimit: Number(limit) || 0 });
      setAuto(res || (await getAutopilot()));
      toast[on ? "success" : "info"](on ? "Mod Latar dihidupkan. Anda boleh tutup tab ini - Chrome mesti kekal terbuka." : "Mod Latar dimatikan.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Gagal");
    } finally { setBusy(false); }
  };

  return (
    <div className="mb-4 rounded-lg border border-indigo-500/30 bg-indigo-500/10 px-4 py-3" data-testid="bg-mode-card">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <MonitorPlay className="mt-0.5 h-5 w-5 shrink-0 text-indigo-300" />
          <div>
            <p className="text-sm font-medium text-indigo-100">Mod Latar Chrome {enabled && <span className="ml-1 rounded bg-emerald-500/20 px-2 py-0.5 text-xs text-emerald-300">AKTIF</span>}</p>
            <p className="text-xs text-indigo-200/70">Helper teruskan post ikut jadual walaupun tab PostPintar ditutup. Chrome mesti kekal terbuka & anda log masuk Facebook.</p>
          </div>
        </div>
        {supported ? (
          <div className="flex items-center gap-2 text-xs text-indigo-100">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Power className="h-4 w-4" />}
            <Switch checked={enabled} disabled={busy} onCheckedChange={(on) => (on ? setConfirm(true) : apply(false))} data-testid="bg-mode-toggle" />
          </div>
        ) : (
          <p className="flex items-start gap-1 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-xs text-amber-100" data-testid="bg-mode-update">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Perlu Helper v{AUTO_BG_MIN_VERSION}+
          </p>
        )}
      </div>

      {supported && (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-xs text-indigo-100">Jarak antara group</Label>
            <Select value={gap} onValueChange={setGap} disabled={enabled}>
              <SelectTrigger className="h-8 w-[150px] border-indigo-500/30 bg-background text-xs" data-testid="bg-mode-gap"><SelectValue /></SelectTrigger>
              <SelectContent>{GAP_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between gap-2">
            <Label className="text-xs text-indigo-100" htmlFor="bg-limit">Had sehari (0 = tiada)</Label>
            <Input id="bg-limit" type="number" min={0} max={500} value={limit} onChange={(e) => setLimit(e.target.value)} disabled={enabled} className="h-8 w-[90px] border-indigo-500/30 bg-background text-xs" data-testid="bg-mode-limit" />
          </div>
        </div>
      )}

      {supported && (
        versionAtLeast(version, AUTO_QUIET_MIN_VERSION)
          ? <QuietHoursControl value={auto?.quiet} onChange={changeQuiet} disabled={busy} />
          : <p className="mt-3 text-xs text-amber-200" data-testid="bg-quiet-update">Waktu Rehat Automatik perlu Helper v{AUTO_QUIET_MIN_VERSION}+ (muat turun di Tetapan).</p>
      )}

      {enabled && (
        <p className="mt-3 text-xs text-indigo-200/80" data-testid="bg-mode-status">
          {auto?.running ? "Sedang post ke satu group..." : auto?.nextRunAt > Date.now() ? `Group seterusnya dalam lebih kurang ${Math.max(1, Math.round((auto.nextRunAt - Date.now()) / 1000))} saat.` : "Menunggu post seterusnya dalam giliran."}
          {" "}Hari ini: {auto?.count || 0}{limit > 0 ? ` / ${limit}` : ""} group. Mod Latar hanya berhenti bila anda tutup suis ini.
        </p>
      )}
      {enabled && auto?.errors > 0 && (
        <p className="mt-2 flex items-start gap-1 text-xs text-amber-200" data-testid="bg-mode-retrying">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Masih AKTIF - sedang cuba semula ({auto.errors}x): {auto.lastReason}
        </p>
      )}

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="border-border bg-card" data-testid="bg-mode-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-400" /> Hidupkan Mod Latar?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>Helper akan buka popup group, isi teks & <b className="text-slate-200">tekan Siaran sendiri</b> ikut giliran, walaupun tab PostPintar ditutup.</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li><b className="text-slate-200">Chrome mesti kekal terbuka</b> di komputer anda. Kalau Chrome/PC ditutup, ia berhenti.</li>
                  <li>Anda mesti sudah log masuk Facebook dalam Chrome yang sama.</li>
                  <li>Popup akan timbul sendiri setiap kali tiba masa post.</li>
                </ul>
                <p className="text-amber-200">Risiko: Facebook mungkin anggap ini automasi/spam dan menyekat akaun anda. Gunakan jarak panjang & had harian.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bg-mode-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={() => apply(true)} data-testid="bg-mode-accept">Saya faham, hidupkan</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
